import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { useGetAuthUser } from "@/api/get-user";
import { ErrorMessage } from "@/app/components/error-message";
import { Card } from "@/components/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { useGetEvent } from "@/features/events/api/get-event";
import { isUserAttendingEvent } from "@/features/events/api/is-user-participating-in-event";
import { EventActions } from "@/features/events/components/event-actions";
import { AttendeesCard } from "@/features/events/components/event-attendees";
import { EventCategoryBadge } from "@/features/events/components/event-category-badge";
import { EventDetails } from "@/features/events/components/event-details";
import { EventOrganiser } from "@/features/events/components/event-organiser";
import { RegisterForEventCard } from "@/features/events/components/register-for-event-card";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import {
  Guest,
  GuestType,
  RoleEnum,
  SessionInvitesStatus,
} from "@classview/shared-types";

import { EventSkeleton } from "../../../features/events/components/event-skeleton";

type DescriptionCardProps = {
  category: string;
  description: string;
  organiserFirstName: string;
  organiserLastName: string;
  organiserAvatarURL: string;
};

function DescriptionCard({
  category,
  description,
  organiserFirstName,
  organiserAvatarURL,
  organiserLastName,
}: DescriptionCardProps) {
  return (
    <Card>
      <Card.Content>
        <div className="flex flex-col gap-4">
          <EventCategoryBadge category={category} />
          <p className="text-sm text-muted-foreground">{description}</p>
          <EventOrganiser
            firstName={organiserFirstName}
            lastName={organiserLastName}
            avatarURL={organiserAvatarURL}
          />
        </div>
      </Card.Content>
    </Card>
  );
}

export function EventPage() {
  const { id } = useParams();
  const {
    data: event,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useGetEvent(id ?? "");
  const { data: user } = useGetAuthUser();
  const { hasPermission } = useOrganisation();

  if (isLoading) {
    return <EventSkeleton />;
  }

  if (isError || !isSuccess) {
    return <ErrorMessage error={error} />;
  }

  const isAttending = isUserAttendingEvent(event, user?.sub ?? "");
  const isCurrentUserOrganiser = event.session.organiser.id === user?.sub;

  const isAttendingBadgeShown = isAttending || isCurrentUserOrganiser;

  const attendees: Guest[] = [
    ...(event.session.attendees ?? []),
    ...(event.session.organiser
      ? [
          {
            id: event.session.organiser.id,
            firstName: event.session.organiser.firstName,
            lastName: event.session.organiser.lastName,
            email: event.session.organiser.email,
            avatarURL: event.session.organiser.avatarURL,
            type: GuestType.User as const,
            isExternal: false,
            isModerator: true,
            isHost: true,
            organisations: [],
          },
        ]
      : []),
  ];
  const hostOrganisation = event.session.invitedOrganisations.find(
    (organisation) => organisation.status === SessionInvitesStatus.HOST,
  );

  const hostOrganisationName = hostOrganisation?.name;
  return (
    <div className="flex flex-col gap-12">
      <Button variant="ghost" asChild className="self-start">
        <Link to="/events/discover">
          <ArrowLeft className="mr-2 size-4" aria-hidden="true" />
          Return to Events
        </Link>
      </Button>
      <div className="flex flex-col gap-6">
        <h1 className="text-2xl font-medium text-foreground">
          <span>
            {event.session.name}{" "}
            {hasPermission([RoleEnum.EVENTS_CREATE]) && (
              <span className="ml-6">
                <EventActions event={event} />
              </span>
            )}
          </span>
          <span className="block text-sm font-normal text-muted-foreground">
            Hosted by {hostOrganisationName}
          </span>
        </h1>
        <EventDetails
          type={event.type}
          startAt={event.session.start}
          endAt={event.session.end}
          isAttending={isAttendingBadgeShown}
        />
      </div>
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex w-full flex-col gap-6">
          {event.bannerS3Path && (
            <Card>
              <AspectRatio ratio={7 / 4}>
                <img
                  src={event.bannerS3Path}
                  alt={event.session.name}
                  className="h-full w-full rounded-2xl object-cover p-1"
                />
              </AspectRatio>
            </Card>
          )}
          <DescriptionCard
            category={event.category}
            description={event.session.description ?? ""}
            organiserFirstName={event.session.organiser.firstName}
            organiserLastName={event.session.organiser.lastName}
            organiserAvatarURL={event.session.organiser.avatarURL}
          />
        </div>
        <div className="flex w-full flex-col gap-6 lg:max-w-[410px]">
          <RegisterForEventCard event={event} />
          <AttendeesCard attendees={attendees} event={event} />
        </div>
      </div>
    </div>
  );
}
